import {
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
  ValueProvider,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { routes } from './app.routes';
import { AppTitleStrategy } from '@core/title/app-title-strategy';
import { credentialsInterceptor } from '@core/auth/credentials.interceptor';
import { sessionInterceptor } from '@core/auth/session.interceptor';
import { errorNotificationInterceptor } from '@core/error-handling/error-notification.interceptor';
import { GlobalErrorHandler } from '@core/error-handling/global-error.handler';
import { csrfInterceptor } from '@core/auth/csrf.interceptor';
import { inFlightDedupInterceptor } from '@core/http/in-flight-dedup.interceptor';
import { BASE_PATH } from '@core/api/generated/variables';
import { API_BASE_PATH } from '@core/config/api-base-path';
import { Parser } from 'marked';

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, character => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    };
    return entities[character] ?? character;
  });
}

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();
  const defaultLinkRenderer = renderer.link;
  const defaultTableRenderer = renderer.table;
  const defaultCodeRenderer = renderer.code;

  renderer.link = function (token) {
    const isRelativeFileLink = /^(?:\.\.?\/|\/)/.test(token.href);
    if (isRelativeFileLink) {
      return Parser.parseInline(token.tokens);
    }
    return defaultLinkRenderer.call(this, token);
  };

  // Wrap every table in its own horizontal scroll container. A table whose
  // columns cannot shrink any further (long paths, many columns) would
  // otherwise widen its parent and break out of the document card instead of
  // scrolling — the styling lives in _markdown.scss under `.md-table-scroll`.
  //
  // `tabindex="0"` makes the scroll region reachable without a mouse
  // (WCAG 2.1.1); the `role`/`aria-label` pair keeps that focus stop from being
  // announced as an unnamed element. All four attributes survive Angular's HTML
  // sanitizer, which ngx-markdown applies to the rendered output.
  renderer.table = function (token) {
    const table = defaultTableRenderer.call(this, token);
    return `<div class="md-table-scroll" role="region" aria-label="Tabelle" tabindex="0">${table}</div>`;
  };

  renderer.code = function (token) {
    return token.lang === 'mermaid' || token.lang === 'mmd'
      ? `<div class="mermaid">${escapeHtml(token.text)}</div>`
      : defaultCodeRenderer.call(this, token);
  };

  return { renderer };
}

/** Base path of the generated OpenAPI client: the same-origin backend API path. */
export const basePathProvider: ValueProvider = {
  provide: BASE_PATH,
  useValue: API_BASE_PATH,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // paramsInheritanceStrategy 'always': child routes such as `overview` inherit
    // the parent `:projectId`, so withComponentInputBinding() receives the input.
    // The default 'emptyOnly' would otherwise yield undefined and a 400 response.
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideHttpClient(
      // Deduplicate before the remaining interceptors build a second identical request.
      withInterceptors([
        inFlightDedupInterceptor,
        credentialsInterceptor,
        csrfInterceptor,
        sessionInterceptor,
        errorNotificationInterceptor,
      ])
    ),
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    basePathProvider,
  ],
};
